import React from "react";
import {
    TableOptions,
    TableState,
    UseSortByOptions,
    UseSortByState,
    useRowSelect,
    useSortBy,
    useTable,
} from "react-table";
import { AclContext } from "../../../../contexts/AclContext";
import { AppSortOrder, IClient, SortConfigProps } from "intake_shared/src/types";
import { cn } from "../../../helpers";
import { Dropdown } from "../Dropdown/Dropdown";
import { Button } from "../Button/Button";
import styles from "./Table.module.scss";
import TablePagination from "./TablePagination";

export interface TableSortBy {
    id: string;
    desc: boolean;
}

const IndeterminateCheckbox = React.forwardRef((props, ref) => {
    const { indeterminate, ...rest } = props as any;
    const defaultRef = React.useRef(null);
    const resolvedRef = (ref || defaultRef) as React.RefObject<HTMLInputElement>;

    React.useEffect(() => {
        if (resolvedRef.current) {
            resolvedRef.current.indeterminate = indeterminate;
        }
    }, [resolvedRef, indeterminate]);

    return (
        <input
            type="checkbox"
            ref={resolvedRef}
            {...rest}
            className={styles.checkbox}
        />
    );
});

function Table({
    columns,
    data,
    allowSelect,
    onSelectActions,
    showActionsInHeader = false,
    styles: styleProps = { wordbreak: false },
    stickyLastColumn = false,
    setSortConfig,
    sortConfig,
    sortableColumns,
    handleRowStyle,
    handleRowClick,
    header,
    footer,
    hasActionsColumn,
    variant = "default",
    assigneeCounts,
    initialSortBy,
    controlledSortColumn,
    onSortByChange,
}: {
    columns: any;
    data: any;
    allowSelect: boolean;
    onSelectActions?: (rows: IClient[]) => JSX.Element[];
    showActionsInHeader?: boolean;
    styles?: { wordbreak: boolean };
    stickyLastColumn?: boolean;
    setSortConfig?: (value: React.SetStateAction<SortConfigProps>) => void;
    sortConfig?: { key: string; direction: string };
    sortableColumns?: string[];
    handleRowStyle?: (row: any) => Record<string, string>;
    handleRowClick?: (row: any) => void;
    header?: React.ReactNode;
    footer?: React.ReactNode;
    hasActionsColumn?: boolean;
    variant?: "default" | "compact";
    assigneeCounts?: Record<string, number>;
    initialSortBy?: TableSortBy;
    controlledSortColumn?: string;
    onSortByChange?: (sortBy?: TableSortBy) => void;
}) {
    const aclContext = React.useContext(AclContext);

    const table = useTable(
        {
            columns,
            data,
            autoResetSortBy: controlledSortColumn ? false : undefined,
            initialState: {
                sortBy: initialSortBy ? [initialSortBy] : [],
            } as Partial<TableState<object> & UseSortByState<object>>,
            useControlledState: (state) => {
                const sortByState = state as TableState<object> &
                    UseSortByState<object>;
                if (!controlledSortColumn) {
                    return state;
                }
                if (initialSortBy) {
                    return {
                        ...state,
                        sortBy: [initialSortBy],
                    };
                }
                if (sortByState.sortBy[0]?.id === controlledSortColumn) {
                    return {
                        ...state,
                        sortBy: [],
                    };
                }
                return state;
            },
        } as TableOptions<object> & UseSortByOptions<object>,
        useSortBy,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) =>
                allowSelect
                    ? [
                          {
                              id: "selection",
                              Header: ({ getToggleAllRowsSelectedProps }: any) => (
                                  <div>
                                      <IndeterminateCheckbox
                                          {...getToggleAllRowsSelectedProps()}
                                      />
                                  </div>
                              ),
                              Cell: ({ row }: any) => (
                                  <div>
                                      <IndeterminateCheckbox
                                          {...row.getToggleRowSelectedProps()}
                                      />
                                  </div>
                              ),
                          },
                          ...columns,
                      ]
                    : [...columns]
            );
        }
    );
    const { getTableBodyProps, headerGroups, rows, prepareRow, setSortBy }: any =
        table;

    const selectedClients: IClient[] = (rows as any[]).reduce(
        (prev: IClient[], curr: any) => {
            curr.isSelected && prev.push(curr.original);
            return prev;
        },
        []
    );
    const showActions =
        allowSelect && selectedClients.length > 0 && Boolean(onSelectActions);
    const actionsDropdown = (align: "start" | "end") => (
        <Dropdown>
            <Dropdown.Trigger>
                <Button
                    className={align === "start" ? styles.actionButton : undefined}
                >
                    Actions
                </Button>
            </Dropdown.Trigger>
            <Dropdown.Content width="120px" align={align}>
                {onSelectActions?.(selectedClients)}
            </Dropdown.Content>
        </Dropdown>
    );

    const handleSort = (key: string) => {
        let direction: AppSortOrder = AppSortOrder.ASC;

        if (
            sortConfig?.key === key &&
            sortableColumns?.includes(key) &&
            sortConfig?.direction === AppSortOrder.ASC
        ) {
            direction = AppSortOrder.DESC;
        }

        if (setSortConfig) {
            setSortBy([]);
            onSortByChange?.(undefined);
            setSortConfig({ key, direction });
        }
    };

    const handleTableSort = (column: any) => {
        const toggleProps = column.getSortByToggleProps();
        if (!onSortByChange || column.id !== controlledSortColumn) {
            if (!initialSortBy) {
                return toggleProps;
            }
            return {
                ...toggleProps,
                onClick: (event: React.MouseEvent) => {
                    onSortByChange?.(undefined);
                    toggleProps.onClick?.(event);
                },
            };
        }

        return {
            onClick: () => {
                setSortBy([]);
                if (!column.isSorted) {
                    onSortByChange({ id: column.id, desc: false });
                    return;
                }

                onSortByChange(
                    column.isSortedDesc ? undefined : { id: column.id, desc: true }
                );
            },
        };
    };

    const assigneeDetails = React.useMemo(() => {
        const counts: Record<string, number> =
            assigneeCounts ??
            (() => {
                const c: Record<string, number> = {};
                (data || []).forEach((item: any) => {
                    const assignee = item?.assigned_to;
                    if (assignee) c[assignee] = (c[assignee] || 0) + 1;
                });
                return c;
            })();
        const colors = aclContext?.intakeConfig?.intakeOfficerColors || {};
        return Object.entries(counts).map(([assignee, count]) => ({
            assignee,
            count,
            color: (colors as Record<string, string>)[assignee],
        }));
    }, [assigneeCounts, data, aclContext?.intakeConfig?.intakeOfficerColors]);

    return (
        <div className={styles.tableContainer}>
            {showActions && !showActionsInHeader && (
                <div className={styles.actionsContainer}>
                    {actionsDropdown("start")}
                </div>
            )}

            <div
                className={cn(styles.tableWrapper, {
                    [styles.tableWrapperActions]:
                        hasActionsColumn || (showActionsInHeader && showActions),
                    [styles.tableWrapperScrollableContent]:
                        stickyLastColumn || (showActionsInHeader && showActions),
                    [styles.stickyLastColumn]: stickyLastColumn,
                })}
            >
                {header && <div className={styles.tableHeader}>{header}</div>}
                {(assigneeDetails.length > 0 ||
                    (showActionsInHeader && showActions)) && (
                    <div className={cn(styles.tableHeader, styles.assigneeSummary)}>
                        <div className={styles.assigneeDetails}>
                            {assigneeDetails.map(({ assignee, count, color }) => (
                                <span
                                    className={styles.assigneeChip}
                                    key={`assignee-${assignee}`}
                                >
                                    <span
                                        className={styles.assigneeDot}
                                        style={{
                                            backgroundColor:
                                                color || "var(--color-primary)",
                                        }}
                                    />
                                    {`${assignee} (${count})`}
                                </span>
                            ))}
                        </div>
                        {showActionsInHeader &&
                            showActions &&
                            actionsDropdown("end")}
                    </div>
                )}

                <div className={styles.tableContent}>
                    <table className={styles.table}>
                        <thead className={styles.fixedHeader}>
                            {headerGroups.map((headerGroup: any) => (
                                <tr
                                    {...headerGroup.getHeaderGroupProps()}
                                    className={styles.headerRow}
                                >
                                    {headerGroup.headers.map((column: any) => (
                                        <th
                                            className={cn(styles.th, {
                                                [styles.compact]:
                                                    variant === "compact",
                                            })}
                                            {...(sortableColumns?.includes(column.id)
                                                ? {
                                                      onClick: () => {
                                                          if (
                                                              sortableColumns?.includes(
                                                                  column.id
                                                              )
                                                          ) {
                                                              handleSort(column.id);
                                                          }
                                                      },
                                                  }
                                                : {
                                                      ...column.getHeaderProps(
                                                          handleTableSort(column)
                                                      ),
                                                  })}
                                        >
                                            {column.render("Header")}
                                            {sortableColumns?.includes(
                                                sortConfig?.key as string
                                            ) ? (
                                                sortConfig?.key === column.id && (
                                                    <span>
                                                        {sortConfig?.direction ===
                                                        AppSortOrder.ASC ? (
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="#576a81"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                className="feather feather-arrow-up"
                                                            >
                                                                <line
                                                                    x1="12"
                                                                    y1="19"
                                                                    x2="12"
                                                                    y2="5"
                                                                />
                                                                <polyline points="5 12 12 5 19 12" />
                                                            </svg>
                                                        ) : (
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="#576a81"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                className="feather feather-arrow-down"
                                                            >
                                                                <line
                                                                    x1="12"
                                                                    y1="5"
                                                                    x2="12"
                                                                    y2="19"
                                                                />
                                                                <polyline points="19 12 12 19 5 12" />
                                                            </svg>
                                                        )}
                                                    </span>
                                                )
                                            ) : column.isSorted ? (
                                                column.isSortedDesc ? (
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="14"
                                                        height="14"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="black"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        className="feather feather-arrow-down"
                                                    >
                                                        <line
                                                            x1="12"
                                                            y1="5"
                                                            x2="12"
                                                            y2="19"
                                                        />
                                                        <polyline points="19 12 12 19 5 12" />
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="14"
                                                        height="14"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="#576a81"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        className="feather feather-arrow-up"
                                                    >
                                                        <line
                                                            x1="12"
                                                            y1="19"
                                                            x2="12"
                                                            y2="5"
                                                        />
                                                        <polyline points="5 12 12 5 19 12" />
                                                    </svg>
                                                )
                                            ) : (
                                                ""
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {rows.map((row: any) => {
                                prepareRow(row);

                                const rawColor =
                                    row?.original?.assigned_to &&
                                    aclContext?.intakeConfig?.intakeOfficerColors?.[
                                        row.original.assigned_to
                                    ];

                                const borderColor = rawColor
                                    ? rawColor
                                    : "transparent";
                                let trStyle: Record<string, string> = {
                                    borderLeftColor: borderColor,
                                    borderLeftStyle: "solid",
                                    borderLeftWidth: rawColor ? "3px" : "0px",
                                };

                                // Apply custom row styling if provided
                                if (handleRowStyle) {
                                    trStyle = { ...trStyle, ...handleRowStyle(row) };
                                }

                                return (
                                    <tr
                                        style={trStyle}
                                        {...row.getRowProps()}
                                        onClick={() =>
                                            handleRowClick?.(row.original)
                                        }
                                        tabIndex={handleRowClick ? 0 : undefined}
                                        onKeyDown={(event) => {
                                            if (
                                                !handleRowClick ||
                                                event.target !==
                                                    event.currentTarget ||
                                                (event.key !== "Enter" &&
                                                    event.key !== " ")
                                            ) {
                                                return;
                                            }
                                            event.preventDefault();
                                            handleRowClick(row.original);
                                        }}
                                        className={cn(styles.tr, {
                                            [styles.clickableRow]: handleRowClick,
                                        })}
                                    >
                                        {row.cells.map((cell: any) => (
                                            <td
                                                {...cell.getCellProps()}
                                                className={cn(styles.td, {
                                                    [styles.wordbreak]:
                                                        styleProps.wordbreak,
                                                    [styles.compact]:
                                                        variant === "compact",
                                                })}
                                            >
                                                {cell.render("Cell")}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {footer && <div className={styles.tableFooter}>{footer}</div>}
            </div>
        </div>
    );
}

export default Table;
export { TablePagination };
